import { FileText } from "lucide-react";
import { isHttps } from "@/lib/disclosureTexts";

/**
 * "Electricity Facts Label / Terms of Service / Your Rights as a Customer" links (Design Specification
 * Section 8.3, 9.3; compliance/01_texas_disclosures.md Section 5). Each link's accessible name contains its
 * visible words plus the plan name so repeated links across a results list are distinguishable (WCAG 2.4.4,
 * 2.5.3). A document with no usable https URL is never rendered as a dead link — it is simply omitted, which
 * is also why a plan with no EFL link must never be shown at all (enforced server-side, catalog/gate.py).
 */
const DOC_DEFS = [
  { key: "efl_document_url", label: "Electricity Facts Label" },
  { key: "terms_document_url", label: "Terms of Service" },
  { key: "yrac_document_url", label: "Your Rights as a Customer" },
];

export default function DocumentLinks({ plan, planName, variant = "row", className = "", testId }) {
  const name = planName || (plan && plan.name) || "this plan";
  const docs = DOC_DEFS.map((d) => ({ ...d, url: plan && plan[d.key] })).filter((d) => isHttps(d.url));
  if (docs.length === 0) return null;

  const sep = variant === "row" ? " · " : null;

  return (
    <nav aria-label={`Plan documents for ${name}`} className={className} data-testid={testId}>
      <ul className={variant === "row" ? "flex flex-wrap items-center gap-x-1 gap-y-1 list-none p-0 m-0" : "list-none p-0 m-0 space-y-2"}>
        {docs.map((d, i) => (
          <li key={d.key} className={variant === "row" ? "inline-flex items-center" : "flex items-center gap-2"}>
            {variant !== "row" && <FileText className="w-4 h-4 text-[#475569]" aria-hidden="true" />}
            <a
              href={d.url.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-sm text-[#0A2540] hover:text-[#15803D] font-medium"
              aria-label={`${d.label} for ${name} (opens in a new tab)`}
              data-testid={`${testId || "doc-link"}-${d.key}`}
            >
              {d.label}
            </a>
            {variant === "row" && i < docs.length - 1 && <span className="text-slate-400 px-1" aria-hidden="true">{sep}</span>}
          </li>
        ))}
      </ul>
    </nav>
  );
}
